import React, {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Button, Card, Col, Container, Form, Row } from "react-bootstrap";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { createSelector } from "reselect";
import { SortingState } from "@tanstack/react-table";

import {
  exportOTeamsCSV,
  fetchTeam,
  updateStatusInTeam as onUpdateStatusTeam,
  TeamExportsCSV,
} from "slices/team/thunk";
import { toast } from "react-toastify";
import TableContainer from "Common/TableContainer";
import { fetchLatestPermissions } from "slices/auth/login/thunk";
import { app_modules } from "helpers/apps_modules_list";
import LoaderErrorWrapper from "Components/LoaderErrorWrapper/LoaderErrorWrapper";
import SearchBox from "Components/SearchBox/SearchBox";

const TeamList = () => {
  const { id } = useParams<{ id: string }>();
  document.title = "Teams List";
  const dispatch = useDispatch<any>();

  const selectorganisation = createSelector(
    (state: any) => state.teams,
    (Teams) => ({
      teams: Teams.teamList, // API's data array for current page
      rowCount: Teams.rowCount, // API's total count
      loading: Teams.loading,
      error: Teams.error,
    })
  );

  const { teams, rowCount, loading, error } = useSelector(selectorganisation);

  const user = useSelector((state: any) => state.Login.user);
  const user_permissions = user?.permissions || [];
  const master_permissions = user_permissions?.find(
    (item: any) => item?.apps_name === app_modules?.user_management?.app_name
  );
  const team_permissions = master_permissions?.modules_permissions?.find(
    (item: any) =>
      item?.modules_name === app_modules?.user_management?.modules?.team
  );

  //*// For redirecting back to this page and get the same paginated pageIndex
  const [searchParams, setSearchParams] = useSearchParams();
  const pageParam = parseInt(searchParams.get("page") || "0");

  const [pageIndex, setPageIndex] = useState(pageParam);
  const [pageSize, setPageSize] = useState(10);
  const [columnFilters, setColumnFilters] = useState<any>([]);
  const [sorting, setSorting] = useState<SortingState>([]);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [colValue, setColValue] = useState<any>();

  // const sortField = sorting[0]?.id ?? "";
  const sortDirection = sorting[0]?.desc ? "desc" : "asc";

  // Fetch data from server on any table state change
  const getBackendSortKey = (key: string) => {
    const map: Record<string, string> = {
      department_name: "department_name",
      organization_name: "organization_name",
      team_name: "team_name",
      created_at: "created_at",
      updated_at: "updated_at",
    };
    return map[key] || key;
  };

  const sortField = sorting[0]?.id ? getBackendSortKey(sorting[0].id) : "";

  useEffect(() => {
    dispatch(
      fetchTeam({
        page: pageIndex + 1,
        pageSize,
        sort: sortField,
        order: sortField ? sortDirection : "",
        search,
        filters: columnFilters,
      })
    );
  }, [
    dispatch,
    pageIndex,
    pageSize,
    sorting,
    search,
    columnFilters,
    colValue,
    sortField,
    sortDirection,
  ]);

  //*// All pagination Issues(resolved) when redirecting from the edit or view page
  // Reset pagenumber to 0 when not first render
  const isFirstRender = useRef(true);
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    setPageIndex(0);
  }, [sorting, pageSize, columnFilters, search]);
  const handlePageChange = (newPage: number) => {
    setPageIndex(newPage);
    setSearchParams({ page: newPage.toString() });
  };
  // Debounced search
  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      setSearch(searchInput);
    }, 500);

    return () => clearTimeout(delayDebounce);
  }, [searchInput]);
  //*//

  useEffect(() => {
    const fetchPermissions = async () => {
      try {
        await dispatch(fetchLatestPermissions()).unwrap();
      } catch (error) {
        console.error("Failed to fetch latest permissions", error);
        // toast.error("Failed to fetch latest permissions");
      }
    };
    fetchPermissions();
  }, [dispatch]);

  const formatDate = (dateString?: string) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };
  // Handle status toggle
  const handleStatusToggle = async (org: any) => {
    try {
      const updatedStatus = org.status === true ? false : true;
      const update_payload = {
        team_name: org?.team_name,
        department: org?.department,
        team_id: org?.team_id,
        status: updatedStatus,
      };
      console.log(update_payload, "update_payload");
      await dispatch(onUpdateStatusTeam(update_payload)).unwrap();
      toast.success(
        `Teams ${updatedStatus ? "activated" : "deactivated"} successfully`
      );
      dispatch(
        fetchTeam({
          page: pageIndex + 1,
          pageSize,
          sort: sortField,
          order: sortField ? sortDirection : "",
          search,
          filters: columnFilters,
        })
      );
    } catch (err: any) {
      console.error("Update Status Error:", err);
      toast.error(err?.message || "Failed to update status");
    }
  };

  // const handleExport = async () => {
  //   const resultAction = await dispatch(exportOTeamsCSV());
  //   if (exportOTeamsCSV.fulfilled.match(resultAction)) {
  //     const { blob, filename } = resultAction.payload;
  //     const url = window.URL.createObjectURL(new Blob([blob]));
  //     const link = document.createElement("a");
  //     link.href = url;
  //     link.setAttribute("download", filename);
  //     document.body.appendChild(link);
  //     link.click();
  //     link.parentNode?.removeChild(link);
  //      window.URL.revokeObjectURL(url)
  //     toast.success('CSV exported successfully')
  //   } else {
  //     toast.error("Failed to export CSV");
  //   }
  // };

  const handleExport = async () => {
    try {
      const { blob, filename } = await TeamExportsCSV({
        search,
        filters: columnFilters,
      });

      const url = window.URL.createObjectURL(new Blob([blob]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.parentNode?.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success("CSV exported successfully");
    } catch (error) {
      toast.error("Failed to export CSV");
    }
  };

  const DynamicAction = team_permissions?.permissions?.Update === true ? [{
    header: "Action",
    enableColumnFilter: false,
    enableSorting: false,
    cell: ({ row }: { row: any }) => (
      <ul className="d-flex gap-2 list-unstyled mb-0">
        <li>
          <Link
            to={`/team-forms/${row?.original?.team_id}`}
            state={{ teams: row.original }}
            className="btn btn-subtle-secondary btn-icon btn-sm edit-item-btn"
          >
            <i className="ph-pencil"></i>
          </Link>
        </li>
      </ul>
    ),
  }] : []

  // Table columns
  const columns = useMemo(
    () => [
      // {
      //   header: "Sr. No.",
      //   id: "sr_no", // important: since there's no accessorKey
      //   enableSorting: false,
      //   enableColumnFilter: false,
      //   cell: ({ row }: { row: any }) => {
      //     return row.index + 1 + pageIndex * pageSize;
      //   },
      // },
      {
        header: "Team",
        accessorKey: "team_name",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "dropdown" as const,
        filterFetchUrl:
          "api/team/column-filter-dropdown/?column_filter=team_name",
      },
      {
        header: "Department",
        accessorKey: "department__department_name",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "dropdown" as const,
        filterFetchUrl:
          "api/team/column-filter-dropdown/?column_filter=department__department_name",
      },
      {
        header: "Organisation",
        accessorKey: "organization__organization_name",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "dropdown" as const,
        filterFetchUrl:
          "api/team/column-filter-dropdown/?column_filter=organization__organization_name",
      },

      {
        header: "Status",
        accessorKey: "status",
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row }: { row: any }) => {
          const org = row.original;
          return (
            <div
              style={{ fontSize: "1.5rem", cursor: "pointer" }}
              onClick={() => handleStatusToggle(org)}
            >
              <i
                className={`bi ${org.status
                    ? "bi-toggle-on text-primary"
                    : "bi-toggle-off text-danger"
                  }`}
              />
            </div>
          );
        },
      },

      {
        header: "Created Date",
        accessorKey: "created_at",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "date" as const,
        cell: ({ row }: { row: any }) => {
          const name = row.original.created_by_name ?? "N/A";
          const date = formatDate(row.original.created_at);
          return `  ${name}  -  ${date}  `; // extra spaces for visual padding
        },
      },
      {
        header: "Updated Date",
        accessorKey: "updated_at",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "date" as const,
        cell: ({ row }: { row: any }) => {
          const name = row.original.updated_by_name ?? "N/A";
          const date = formatDate(row.original.updated_at);
          return `  ${name}  -  ${date}  `; // spaces before/after the name
        },
      },
      ...DynamicAction,
      // {
      //   header: "Action",
      //   enableColumnFilter: false,
      //   enableSorting: false,
      //   cell: ({ row }: { row: any }) => (
      //     <ul className="d-flex gap-2 list-unstyled mb-0">
      //       <li>
      //         <Link
      //           to={`/team-forms/${row?.original?.team_id}`}
      //           className="btn btn-subtle-secondary btn-icon btn-sm edit-item-btn"
      //         >
      //           <i className="ph-pencil"></i>
      //         </Link>
      //       </li>
      //     </ul>
      //   ),
      // },
    ],
    [dispatch, teams, handleStatusToggle, team_permissions?.permissions]
  );

  return (
    <Fragment>
      <div className="page-content">
        <Container fluid>
          <Row id="invoiceList">
            <Col lg={12}>
              <Card>
                <Card.Header>
                  <Row className="align-items-center g-2">
                    <Col lg={3} className="me-auto">
                      <h6 className="card-title mb-0">Team</h6>
                    </Col>

                    {/* Search Input Box (reusable)*/}
                    <Col xl={2} md={3}>
                      <SearchBox
                        value={searchInput}
                        onChange={setSearchInput}
                      />
                    </Col>

                    <Col className="col-md-auto">
                      <div className="hstack gap-2">
                        <Button
                          className="btn btn-subtle-danger d-none"
                          id="remove-actions"
                        >
                          <i className="ri-delete-bin-2-line"></i>
                        </Button>
                        {/* {
                          team_permissions?.permissions?.View === true && */}
                        {/* <div
                            className="hstack gap-2 btn btn-light"
                            onClick={handleExport}
                            style={{ cursor: "pointer" }}
                          >
                            <i className="bi bi-box-arrow-up align-baseline me-1"></i>{" "}
                            Export
                          </div> */}
                        {/* } */}
                        {
                          team_permissions?.permissions?.Create === true &&
                          <Link className="btn btn-primary" to="/team-forms">
                            <i className="bi bi-plus-circle align-baseline me-1"></i>
                            Add Team
                          </Link>
                        }
                      </div>
                    </Col>
                  </Row>
                </Card.Header>
                <Card.Body className="mt-3">
                  <LoaderErrorWrapper loading={loading} error={error}>
                    <TableContainer
                      columns={columns}
                      data={teams && teams.length > 0 ? teams : []}
                      totalRecords={rowCount}
                      pageIndex={pageIndex}
                      pageSize={pageSize}
                      // onPageChange={setPageIndex}
                      onPageChange={handlePageChange} // for persisted pagination across pages
                      onPageSizeChange={setPageSize}
                      columnFilters={columnFilters}
                      setColumnFilters={setColumnFilters}
                      colValue={setColValue}
                      sorting={sorting}
                      onSortingChange={setSorting}
                      isBordered={false}
                      isPagination={true}
                      isGlobalFilter={false}
                      SearchPlaceholder="Search teams ..."
                      manualSorting={true}
                    />
                  </LoaderErrorWrapper>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </Fragment>
  );
};

export default TeamList;
